import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Copy, CheckCircle, WarningCircle, FloppyDisk, PlugsConnected } from "@phosphor-icons/react";

const empty = {
  enabled: false,
  test_mode: true,
  merchant_id: "",
  merchant_key: "",
  merchant_salt: "",
  no_installment: "0",
  max_installment: "0",
  timeout_limit: 30,
  pro_price_try: 599,
  business_price_try: 999,
  display_title: "Kredi / Banka Kartı (PayTR)",
  display_description: "Kredi ve banka kartınızla PayTR güvenli ödeme altyapısı üzerinden ödeme yapabilirsiniz.",
  language: "tr",
};

export default function AdminPaymentMethods() {
  const [form, setForm] = useState(empty);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/payment-methods/paytr");
      setMeta(data);
      setForm((f) => ({
        ...f,
        enabled: !!data.enabled,
        test_mode: !!data.test_mode,
        merchant_id: data.merchant_id || "",
        merchant_key: "",
        merchant_salt: "",
        no_installment: String(data.no_installment ?? "0"),
        max_installment: String(data.max_installment ?? "0"),
        timeout_limit: Number(data.timeout_limit || 30),
        pro_price_try: Number(data.pro_price_try || 1490),
        business_price_try: Number(data.business_price_try || 2990),
        display_title: data.display_title || f.display_title,
        display_description: data.display_description || f.display_description,
        language: data.language || "tr",
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "PayTR ayarları yüklenemedi");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/payment-methods/paytr", form);
      toast.success("PayTR ayarları kaydedildi");
      setMeta(data);
      setForm((f) => ({ ...f, merchant_key: "", merchant_salt: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ayarlar kaydedilemedi");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/payment-methods/paytr/test");
      toast.success(data.message || "PayTR bağlantısı başarılı");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "PayTR bağlantı testi başarısız");
      await load();
    } finally { setTesting(false); }
  };

  const copyCallback = async () => {
    await navigator.clipboard.writeText(meta.callback_url || "");
    toast.success("Callback URL kopyalandı");
  };

  if (loading) return <div className="p-8 text-slate-500">Ödeme ayarları yükleniyor…</div>;

  const configured = !!meta.configured;
  const liveReady = configured && form.enabled;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-8 space-y-6" data-testid="admin-payment-methods">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-blue-600 font-semibold">Yönetim</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">Ödeme Yöntemleri</h1>
          <p className="text-sm text-slate-500 mt-2">PayTR Sanal POS bağlantısını kod veya .env düzenlemeden buradan yönetin.</p>
        </div>
        <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-semibold ${liveReady ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
          {liveReady ? <CheckCircle size={18} weight="fill" /> : <WarningCircle size={18} weight="fill" />}
          {liveReady ? "PayTR Aktif" : configured ? "Yapılandırıldı / Pasif" : "Yapılandırma Gerekli"}
        </div>
      </div>

      <section className="bg-white border border-slate-200 shadow-sm rounded-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 text-blue-600 flex items-center justify-center rounded-sm"><CreditCard size={22} weight="duotone" /></div>
          <div><div className="font-bold text-slate-900">PayTR Sanal POS — iFrame API</div><div className="text-xs text-slate-500">Kredi / banka kartı ödeme altyapısı</div></div>
        </div>

        <div className="p-5 grid lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <Toggle label="PayTR'ı Aktif Et" text="Müşterilerin abonelik ekranında PayTR ile ödeme yapmasına izin verir." checked={form.enabled} onChange={(v) => set("enabled", v)} />
            <Toggle label="Test Modu" text="Canlıya geçmeden önce test ödemeleri için açık tutun." checked={form.test_mode} onChange={(v) => set("test_mode", v)} />

            <Field label="Mağaza No (Merchant ID)"><input value={form.merchant_id} onChange={(e) => set("merchant_id", e.target.value)} className="input" autoComplete="off" /></Field>
            <Field label="Mağaza Parola (Merchant Key)" hint={meta.merchant_key_configured ? `Kayıtlı: ${meta.merchant_key_masked}. Değiştirmeyeceksen boş bırak.` : "Henüz kayıtlı değil."}><input type="password" value={form.merchant_key} onChange={(e) => set("merchant_key", e.target.value)} className="input" autoComplete="new-password" placeholder={meta.merchant_key_configured ? "Değiştirmek için yeni değer gir" : "Merchant Key"} /></Field>
            <Field label="Mağaza Gizli Anahtar (Merchant Salt)" hint={meta.merchant_salt_configured ? `Kayıtlı: ${meta.merchant_salt_masked}. Değiştirmeyeceksen boş bırak.` : "Henüz kayıtlı değil."}><input type="password" value={form.merchant_salt} onChange={(e) => set("merchant_salt", e.target.value)} className="input" autoComplete="new-password" placeholder={meta.merchant_salt_configured ? "Değiştirmek için yeni değer gir" : "Merchant Salt"} /></Field>
          </div>

          <div className="space-y-5">
            <Field label="Pro Paket Fiyatı"><div className="relative"><input type="number" min="0" step="0.01" value={form.pro_price_try} onChange={(e) => set("pro_price_try", Number(e.target.value))} className="input pr-12" /><span className="absolute right-3 top-2.5 text-sm text-slate-500">₺</span></div></Field>
            <Field label="Business Paket Fiyatı"><div className="relative"><input type="number" min="0" step="0.01" value={form.business_price_try} onChange={(e) => set("business_price_try", Number(e.target.value))} className="input pr-12" /><span className="absolute right-3 top-2.5 text-sm text-slate-500">₺</span></div></Field>
            <Field label="Taksit Ayarları"><select value={form.max_installment} onChange={(e) => set("max_installment", e.target.value)} className="input"><option value="0">Tüm PayTR taksit seçenekleri</option>{[2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={String(n)}>En fazla {n} taksit</option>)}</select></Field>
            <Toggle label="Taksitsiz Ödeme Zorunlu" text="Açıldığında PayTR'a no_installment=1 gönderilir." checked={form.no_installment === "1"} onChange={(v) => set("no_installment", v ? "1" : "0")} />
            <Field label="Timeout (saniye)"><input type="number" min="5" max="300" value={form.timeout_limit} onChange={(e) => set("timeout_limit", Number(e.target.value))} className="input" /></Field>
            <Field label="Ödeme Başlığı"><input value={form.display_title} onChange={(e) => set("display_title", e.target.value)} className="input" /></Field>
            <Field label="Ödeme Açıklaması"><textarea rows="3" value={form.display_description} onChange={(e) => set("display_description", e.target.value)} className="input resize-none" /></Field>
          </div>
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Bildirim / Callback URL</div>
          <div className="flex gap-2"><input readOnly value={meta.callback_url || ""} className="input font-mono text-xs bg-white" /><button onClick={copyCallback} className="px-3 border border-slate-300 bg-white hover:bg-slate-100"><Copy size={18} /></button></div>
          <p className="text-xs text-slate-500 mt-2">PayTR panelinde bildirim URL gerekiyorsa bu adresi kullanın. Uygulama callback imzasını sunucu tarafında doğrular.</p>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex flex-col sm:flex-row gap-3 sm:justify-between">
          <button onClick={test} disabled={testing || !configured} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 font-semibold text-sm"><PlugsConnected size={18} />{testing ? "PayTR Test Ediliyor…" : "PayTR Bağlantısını Test Et"}</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm disabled:opacity-50"><FloppyDisk size={18} />{saving ? "Kaydediliyor…" : "Ayarları Kaydet"}</button>
        </div>
      </section>

      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Status ok={form.enabled} label="Sistem" value={form.enabled ? "Aktif" : "Pasif"} />
        <Status ok={configured} label="Kimlik Bilgileri" value={configured ? "Tamam" : "Eksik"} />
        <Status ok={!!meta.callback_url} label="Callback" value="Hazır" />
        <Status ok={meta.last_test?.ok} label="Son Test" value={meta.last_test?.message || "Henüz test edilmedi"} />
      </section>

      <style>{`.input{width:100%;border:1px solid #cbd5e1;background:#fff;padding:.62rem .75rem;border-radius:2px;font-size:.875rem;outline:none}.input:focus{border-color:#2563eb;box-shadow:0 0 0 2px rgba(37,99,235,.08)}`}</style>
    </div>
  );
}

function Field({ label, hint, children }) { return <label className="block"><div className="text-sm font-semibold text-slate-700 mb-1.5">{label}</div>{children}{hint && <div className="text-xs text-slate-500 mt-1.5">{hint}</div>}</label>; }
function Toggle({ label, text, checked, onChange }) { return <div className="flex items-start justify-between gap-4 border border-slate-200 p-3 rounded-sm"><div><div className="text-sm font-semibold text-slate-800">{label}</div><div className="text-xs text-slate-500 mt-1">{text}</div></div><button type="button" onClick={() => onChange(!checked)} className={`relative w-11 h-6 rounded-full transition ${checked ? "bg-blue-600" : "bg-slate-300"}`}><span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${checked ? "left-6" : "left-1"}`} /></button></div>; }
function Status({ ok, label, value }) { return <div className="bg-white border border-slate-200 p-4 rounded-sm"><div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">{ok ? <CheckCircle className="text-emerald-600" size={16} weight="fill" /> : <WarningCircle className="text-amber-500" size={16} weight="fill" />}{label}</div><div className="font-semibold text-slate-800 mt-2 text-sm truncate" title={value}>{value}</div></div>; }
